import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowLeft } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

const CartPage: React.FC = () => {
  const { items, removeItem, updateQuantity, clearCart, totalItems, totalPrice } = useCart();
  const navigate = useNavigate();
  const [quantityInputs, setQuantityInputs] = useState<Record<string, string>>({});

  useEffect(() => {
    const nextInputs: Record<string, string> = {};
    items.forEach((item) => {
      nextInputs[item.cartKey] = String(item.quantity);
    });
    setQuantityInputs(nextInputs);
  }, [items]);

  const commitQuantityInput = (cartKey: string, fallbackQuantity: number) => {
    const rawValue = quantityInputs[cartKey];
    const parsedQuantity = Number.parseInt((rawValue || '').trim(), 10);

    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      setQuantityInputs((prev) => ({ ...prev, [cartKey]: String(fallbackQuantity) }));
      return;
    }

    updateQuantity(cartKey, parsedQuantity);
  };

  if (items.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 bg-gradient-to-b from-pink-50 via-rose-50 to-pink-100 dark:bg-dark-bg dark:from-dark-bg dark:via-dark-bg dark:to-dark-bg min-h-screen transition-colors duration-300">
        <div className="text-center">
          <ShoppingBag className="mx-auto h-24 w-24 text-gray-400 dark:text-gray-600 mb-6" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text mb-4">سلة التسوق فارغة</h2>
          <p className="text-gray-600 dark:text-dark-textSecondary mb-8">لم تقم بإضافة أي منتجات إلى سلة التسوق بعد</p>
          <Link
            to="/"
            className="inline-flex items-center space-x-2 rounded-lg text-white bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 shadow-md hover:shadow-lg transition-all duration-300 px-4 py-2"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>العودة للتسوق</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-gradient-to-b from-pink-50 via-rose-50 to-pink-100 dark:bg-dark-bg dark:from-dark-bg dark:via-dark-bg dark:to-dark-bg min-h-screen transition-colors duration-300">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text">سلة التسوق</h1>
        <p className="text-gray-600 dark:text-dark-textSecondary mt-2">لديك {totalItems} منتج في السلة</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-dark-surface rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-4">المنتجات المختارة</h2>

              <div className="space-y-4">
                {items.map((item) => (
                  <div key={item.cartKey} className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between space-y-4 sm:space-y-0 p-4 bg-gray-50 dark:bg-dark-hover rounded-lg sm:gap-6">
                    <div className="flex items-center gap-4 w-full sm:flex-1 sm:min-w-0">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                        onError={(e) => {
                          e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2Zz4KPHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNCM0IzQjMiLz4KPHBhdGggZD0iTTMwIDMwSDcwVjcwSDMwVjMwWiIgZmlsbD0iI0QxRDVEQiIvPgo8cGF0aCBkPSJNNDAgNDBIMjBWNjBIMjBWNDBaIiBmaWxsPSIjQzdDQ0Q3Ii8+Cjwvc3ZnPgo=';
                        }}
                      />

                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text truncate">{item.name}</h3>
                        {item.selectedColor && (
                          <div className="mt-1 flex items-center gap-2 text-sm text-gray-600 dark:text-dark-textSecondary">
                            <span>اللون:</span>
                            <span
                              className="inline-block h-4 w-4 rounded-full border border-gray-300 dark:border-dark-border"
                              style={{ backgroundColor: item.selectedColor }}
                              aria-label={`Selected color ${item.selectedColor}`}
                            />
                            <span>{item.selectedColor}</span>
                          </div>
                        )}
                        <p className="text-lg font-bold text-pink-600 dark:text-pink-400">₪{item.price}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between w-full sm:w-auto sm:gap-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.cartKey, item.quantity - 1)}
                          className="p-1 rounded-full bg-gray-200 dark:bg-dark-surface hover:bg-gray-300 dark:hover:bg-dark-bg transition-colors"
                        >
                          <Minus className="h-4 w-4 text-gray-600 dark:text-dark-textSecondary" />
                        </button>

                        <input
                          type="number"
                          min={1}
                          inputMode="numeric"
                          value={quantityInputs[item.cartKey] ?? String(item.quantity)}
                          onChange={(e) => {
                            setQuantityInputs((prev) => ({ ...prev, [item.cartKey]: e.target.value }));
                          }}
                          onBlur={() => commitQuantityInput(item.cartKey, item.quantity)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              (e.target as HTMLInputElement).blur();
                            }
                          }}
                          className="w-14 text-center text-lg font-medium text-gray-900 dark:text-dark-text bg-white dark:bg-dark-surface border border-gray-300 dark:border-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                          aria-label={`Quantity for ${item.name}`}
                        />

                        <button
                          onClick={() => updateQuantity(item.cartKey, item.quantity + 1)}
                          className="p-1 rounded-full bg-gray-200 dark:bg-dark-surface hover:bg-gray-300 dark:hover:bg-dark-bg transition-colors"
                        >
                          <Plus className="h-4 w-4 text-gray-600 dark:text-dark-textSecondary" />
                        </button>
                      </div>

                      <div className="text-right">
                        <p className="text-lg font-bold text-pink-600 dark:text-pink-400">
                          ₪{(item.price * item.quantity).toFixed(2)}
                        </p>
                        <button
                          onClick={() => removeItem(item.cartKey)}
                          className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors mt-1"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-dark-border">
                <button
                  onClick={clearCart}
                  className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 font-medium transition-colors"
                >
                  مسح السلة
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-dark-surface rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-6 sticky top-8">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-4">ملخص الطلب</h2>

            <div className="space-y-3 mb-6">
              <div className="flex justify-between text-gray-600 dark:text-dark-textSecondary">
                <span>عدد المنتجات:</span>
                <span>{totalItems}</span>
              </div>
              <div className="border-t border-gray-200 dark:border-dark-border pt-3">
                <div className="flex justify-between text-lg font-bold text-gray-900 dark:text-dark-text">
                  <span>الإجمالي بدون الشحن:</span>
                  <span>₪{totalPrice.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <button
              onClick={() => navigate('/checkout')}
              className="w-full py-3 text-lg font-semibold rounded-lg text-white bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 shadow-md hover:shadow-lg transition-all duration-300"
            >
              إتمام الطلب
            </button>

            <div className="mt-4 text-center">
              <Link
                to="/"
                className="text-pink-600 dark:text-pink-400 hover:text-pink-700 dark:hover:text-pink-300 text-sm transition-colors"
              >
                <ArrowLeft className="inline h-4 w-4 ml-1" />
                متابعة التسوق
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
